import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GroupModel, PriceModel } from '@root/router/properties/properties.props';
import {
  HouseListResultInterface,
  GroupListItemInterface,
  GroupListResultInterface,
  BindHostParamsInterface,
  IEditTitle,
  HttpResponseInterface,
  IPublishZhenguoInterface,
  IReadThirdpartyHouseInterface,
  IUpdateHouseInterface,
  IGetHouseOrderListInterface,
  IEditHouseOrderInterface,
  IGetSubAccountListInterface,
} from '@root/router/properties/http-interface';
import { Observable } from 'rxjs';
import _ from 'lodash';
import { Subject } from 'rxjs';

const BIND_HOST_API = '/api/house/bind_host';
const HOUSE_CREATE_API = '/api/house/create';
const GROUP_LIST_API = '/api/house_group/group_list';
const CREATE_GROUP_API = '/api/house_group/create';
const DELETE_GROUP_API = '/api/house_group/delete';
const UPDATE_GROUP_API = '/api/house_group/modify';
const HOUSE_LIST_API = '/api/house/search';
const ADD_HOUSES_TO_GROUPS_API = '/api/house_group/add_house';
const REMOVE_HOUSE_FROM_GROUPS_API = '/api/house_group/remove_house';
const EDIT_TITLE_API = '/api/house/title_alias';
const PUBLISH_ZHNEGUO_API = '/api/house/publish_zhenguo';
const READ_THIRDPARTY_HOUSE_API = '/api/house/read_thirdparty_house';
const UPDATE_HOUSE_API = '/api/house/update';
const GET_SETTINGS_API = '/api/house_status_setting/get';
const GET_HOUSE_ORDER_LIST_API = '/api/house/order_list';
const EDIT_HOUSE_ORDER_API = '/api/house/edit_order';
const RESTORE_DEFAULT_SORT_API = '/api/house/restore_default_sort';
const SUB_ACCOUNT_LIST_API = '/api/sub_account/list';
const SYNC_MASTER_GROUP_API = '/api/house_group/sync_master_group';

@Injectable({
  providedIn: 'root',
})
export class PropertiesService {
  groupRaw$ = new Subject<{ type: 'change' | 'update'; value?: any }>();

  auditRaw$ = new Subject();

  constructor(private http: HttpClient) {}

  changeAudit() {
    this.auditRaw$.next(null);
  }

  /**
   * 获取房源列表
   * @param params HouseListParamsInterface
   * @return Observable<HouseListResultInterface>
   */
  getHouseList(params): Observable<HouseListResultInterface> {
    // const query = new HttpParams()
    //   .append("page", `${params.page || 1}`)
    //   .append("page_size", `${params.page_size}`)
    //   .append("title", `${params.title}`)
    //   .append("group_id", `${params.group_id}`)
    //   .append("house_type_id", `${params.house_type_id}`)
    //   .append("operator_relation_id", `${params.operator_relation_id}`);

    return this.http.get(HOUSE_LIST_API, { params }) as Observable<HouseListResultInterface>;
  }

  saveTags(params) {
    return this.http.post('/api/house/save_tags', params) as Observable<HttpResponseInterface>;
  }

  /**
   * 获取全部房源分组
   */
  getGroupList(): Observable<GroupListResultInterface> {
    return this.http.get(GROUP_LIST_API) as Observable<GroupListResultInterface>;
  }

  /**
   * 创建分组
   * @param params
   */
  createGroup(params: { name: string }): Observable<HttpResponseInterface> {
    return this.http.post(CREATE_GROUP_API, params) as Observable<HttpResponseInterface>;
  }

  updateGroup(params: { group_id: number; name: string }) {
    return this.http.post(UPDATE_GROUP_API, params) as Observable<HttpResponseInterface>;
  }

  deleteGroup(params: { group_id: number }) {
    return this.http.post(DELETE_GROUP_API, params) as Observable<HttpResponseInterface>;
  }

  moveHousesToGroups(params: { group_ids: string; house_ids: string }): Observable<HttpResponseInterface> {
    return this.http.post(ADD_HOUSES_TO_GROUPS_API, params) as Observable<HttpResponseInterface>;
  }

  removeHouseFromGroups(params: { group_ids: string; house_id: string | number }): Observable<HttpResponseInterface> {
    return this.http.post(REMOVE_HOUSE_FROM_GROUPS_API, params) as Observable<HttpResponseInterface>;
  }

  bindHost(params: BindHostParamsInterface) {
    return this.http.post(BIND_HOST_API, params);
  }

  /** 编辑房源别名 */
  editTitle(params: IEditTitle) {
    return this.http.post(EDIT_TITLE_API, params) as Observable<HttpResponseInterface>;
  }

  /**
   * 将后端数据转换成HouseModel数组
   * @deprecated
   * @param data { list: Array<HouseListResultInterface> }
   */
  toHouseModelList(res: HouseListResultInterface): Array<any> {
    const list = (res.data && res.data.list) || [];
    return list.map((result: any) => {
      return {
        id: result.id,
        cover_picture: result.cover_picture,
        house_title: result.house_title === '' ? '未设置房源名称' : result.house_title,
        groups: result.house_groups.map((group) => {
          return new GroupModel({
            id: group.house_group_id,
            name: group.name,
          });
        }),
        prices: result.house_prices.map((price) => new PriceModel(price)),
        house_type_title: result.house_type.title,
        checked: false,
        has_house_prices: result.has_house_prices,
        is_master_house: result.is_master_house,
        is_slave_house: result.is_slave_house,
        stop_auto_managed: result.stop_auto_managed,
        checkin_guide: result.checkin_guide,
        tags: result.tags?.map((tag) => tag.id),
      };
    });
  }

  /**
   * 将后端数据转换成GroupModel数组
   * @param data { list: Array<GroupListResultInterface> }
   */
  toGroupModelList(res: GroupListResultInterface): Array<GroupModel> {
    const list = (res.data && res.data.list) || [];
    return list.map((result: GroupListItemInterface) => {
      return new GroupModel({
        id: result.group_id,
        name: result.name,
      });
    });
  }

  updateGroupList(list) {
    this.groupRaw$.next({ type: 'update', value: list });
  }

  changeGroup(group) {
    this.groupRaw$.next({ type: 'change', value: group });
  }

  fetchGroupList() {
    this.getGroupList().subscribe((res) => {
      const list = this.toGroupModelList(res);
      this.updateGroupList(list);
    });
  }

  /** 设置榛果直连关系 */
  publishZhenguo(params: IPublishZhenguoInterface) {
    return this.http.post(PUBLISH_ZHNEGUO_API, params) as Observable<HttpResponseInterface>;
  }

  /** 读取三方房源 */
  readThirdpartyHouse(params: IReadThirdpartyHouseInterface) {
    return this.http.post(READ_THIRDPARTY_HOUSE_API, params) as Observable<HttpResponseInterface>;
  }

  /** 创建房间 */
  // createHouse(params: ICreateHouseInterface) {
  //   return this.http.post(HOUSE_CREATE_API, params) as Observable<
  //     HttpResponseInterface
  //   >;
  // }

  // 更新房间信息：名称、照片
  updateHouse(params: IUpdateHouseInterface) {
    return this.http.post(UPDATE_HOUSE_API, params) as Observable<HttpResponseInterface>;
  }

  /** 获取房态配置 */
  getSettings() {
    const query = new HttpParams();
    return this.http.get(GET_SETTINGS_API, { params: query }) as Observable<HttpResponseInterface>;
  }

  /** 获取房间排序列表 */
  getHouseOrderList(params: IGetHouseOrderListInterface) {
    const query = new HttpParams().append('format', `${params.format}`);
    return this.http.get(GET_HOUSE_ORDER_LIST_API, {
      params: query,
    }) as Observable<HttpResponseInterface>;
  }

  /** 编辑房间排序 */
  editHouseOrder(params: IEditHouseOrderInterface) {
    return this.http.post(EDIT_HOUSE_ORDER_API, params) as Observable<HttpResponseInterface>;
  }

  /** 房源恢复默认排序 */
  restoreHouseDefaultSort() {
    return this.http.post(RESTORE_DEFAULT_SORT_API, {}) as Observable<HttpResponseInterface>;
  }

  /** 拥有子账号列表 */
  getSubAccountList(params: IGetSubAccountListInterface) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('format', 'house_filter');
    return this.http.get(SUB_ACCOUNT_LIST_API, { params: query }) as Observable<HttpResponseInterface>;
  }

  /** 子账号同步主账号房间分组 */
  syncMasterGroup() {
    return this.http.post(SYNC_MASTER_GROUP_API, {}) as Observable<HttpResponseInterface>;
  }
}
